import { unicodeBlocks } from '@gelatoas/design-editor-fonts';

import fontManager, { FontInfo } from 'editor/src/store/fonts/fontManager';
import { FontDefinition } from 'editor/src/store/fonts/types';
import loadFont from 'editor/src/store/fonts/utils/loadFont';

import { captureMessage } from 'editor/src/logger';

// Fonts are listed for a Unicode block only above 15% coverage, so a block can have no font (⭐) or
// one that lacks the glyph (✨ in M PLUS 1p). Noto Emoji covers most symbols and emoji: try it last, in
// Bold, the weight the emoji blocks list first, so every emoji draws in one weight. Preflight's
// nodejs-tools/src/utils.ts getFont picks the same font, so print matches the canvas.
const LAST_RESORT_FONT_FILE = 'NotoEmoji-Bold';

const fetched: { [fontFile: string]: Promise<FontInfo> } = {};
const fontCacheByBlock: { [block: number]: FontDefinition } = {};
const reportedChars = new Set<string>();
let lastFallbackFont: FontDefinition | undefined;

function findWorkingFont(char: string) {
  const unicode = char.codePointAt(0) ?? 0;
  const blockIndex = unicodeBlocks.findIndex(([start, end]) => unicode >= start && unicode <= end);

  if (!fontCacheByBlock[blockIndex]) {
    const font = fontManager.fontDefinitions.find((font) => font.metadata.unicodeBlockIndexes.includes(blockIndex));
    if (!font) {
      return undefined;
    }
    fontCacheByBlock[blockIndex] = font;
  }

  return fontCacheByBlock[blockIndex];
}

function getLastResortFont() {
  return fontManager.fontDefinitions.find((font) => font.metadata.fontFile === LAST_RESORT_FONT_FILE);
}

// One code point, including an astral one such as 👏 that takes two UTF-16 units.
function isSingleCodePoint(char: string) {
  return char.length === 1 || (char.length === 2 && (char.codePointAt(0) ?? 0) > 0xffff);
}

// according to the opentype fix that is not released yet https://github.com/opentypejs/opentype.js/pull/632/files
// 0 is the standard code for the .notdefglyph, to be displayed as a fallback. https://learn.microsoft.com/en-us/typography/opentype/spec/cmap
const fontHasChar = (fontInfo: FontInfo | undefined, char: string) => {
  return !!fontInfo && fontInfo.obj.charToGlyphIndex(char) > 0;
};

function loadFallbackFont(fontDefinition: FontDefinition, onFallbackFontLoaded: () => void) {
  const { fontFile } = fontDefinition.metadata;
  fontManager.fallbackFonts.add(fontFile);
  const promise = fetched[fontFile] ?? loadFont(fontDefinition, { executor: 'FabricPathText' });
  fetched[fontFile] = promise;

  // Every caller redraws, not just the first: a design load measures text on a throwaway object
  // first. getFont then takes this font if it has the glyph, or moves on to the next one.
  promise
    .then(() => onFallbackFontLoaded())
    .catch(() => {
      // font load error are already captured by loadFont
    })
    .finally(() => {
      delete fetched[fontFile];
    });
}

function getFont(fontFamily: string, char: string, onFallbackFontLoaded: () => void): FontInfo | undefined {
  const font = fontManager.loadedFontMap[fontFamily] as FontInfo | undefined;
  if (!font || !isSingleCodePoint(char) || fontHasChar(font, char)) {
    return font;
  }

  const lastFallbackFontInfo = lastFallbackFont && fontManager.loadedFontMap[lastFallbackFont.metadata.fontFile];
  if (fontHasChar(lastFallbackFontInfo, char)) {
    return lastFallbackFontInfo;
  }

  const blockFont = findWorkingFont(char);
  // Until a font with the glyph is found, draw with the block's font once loaded, else the text font.
  const pendingFont = (blockFont && fontManager.loadedFontMap[blockFont.metadata.fontFile]) || font;
  // The first candidate that is not loaded yet, or that has the glyph.
  const candidate = [blockFont, getLastResortFont()].find((fontDefinition) => {
    if (!fontDefinition) {
      return false;
    }
    const fontDefinitionInfo = fontManager.loadedFontMap[fontDefinition.metadata.fontFile];
    return !fontDefinitionInfo || fontHasChar(fontDefinitionInfo, char);
  });

  if (candidate) {
    const candidateInfo = fontManager.loadedFontMap[candidate.metadata.fontFile];
    if (candidateInfo) {
      // Never Noto Emoji: it also has symbols such as ☀ and ▶ whose blocks list another font, and as
      // lastFallbackFont it would draw them after any emoji drawn earlier in the session.
      if (candidate.metadata.fontFile !== LAST_RESORT_FONT_FILE) {
        lastFallbackFont = candidate;
      }
      return candidateInfo;
    }
    loadFallbackFont(candidate, onFallbackFontLoaded);
    return pendingFont;
  }

  if (!reportedChars.has(char)) {
    reportedChars.add(char);
    captureMessage('no fallback font', { extra: { char, codePoint: char.codePointAt(0)?.toString(16) } });
  }
  return pendingFont;
}

export default getFont;
